import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";

interface AddClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const AddClientDialog = ({
  open,
  onOpenChange,
  onSuccess,
}: AddClientDialogProps) => {
  const { toast } = useToast();
  const { organization } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    clientName: "",
    clientUid: "",
    mandateType: "full_bookkeeping",
    industryCode: "",
    vatLiable: true,
    vatMethod: "effective",
    fiscalYearEnd: "12-31",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.clientName.trim()) {
      toast({
        title: "Client Name Required",
        description: "Please enter a client name.",
        variant: "destructive",
      });
      return;
    }

    if (!organization?.id) {
      toast({
        title: "Error",
        description: "Organization not found.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from("mandates").insert({
        organization_id: organization.id,
        client_name: formData.clientName,
        client_uid: formData.clientUid || null,
        mandate_type: formData.mandateType,
        industry_code: formData.industryCode || null,
        vat_liable: formData.vatLiable,
        vat_method: formData.vatMethod,
        fiscal_year_end: `1970-${formData.fiscalYearEnd}`,
        status: "active",
        learning_stage: "teaching",
        confidence_score: 0,
        automation_rate: 0,
      });

      if (error) throw error;

      toast({
        title: "Client Added",
        description: `${formData.clientName} has been successfully added.`,
      });

      setFormData({
        clientName: "",
        clientUid: "",
        mandateType: "full_bookkeeping",
        industryCode: "",
        vatLiable: true,
        vatMethod: "effective",
        fiscalYearEnd: "12-31",
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add client. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
            <DialogDescription>
              Create a new client mandate and configure accounting settings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="clientName">Client Name *</Label>
                <Input
                  id="clientName"
                  value={formData.clientName}
                  onChange={(e) =>
                    setFormData({ ...formData, clientName: e.target.value })
                  }
                  placeholder="Company Name AG"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientUid">Swiss UID</Label>
                <Input
                  id="clientUid"
                  value={formData.clientUid}
                  onChange={(e) =>
                    setFormData({ ...formData, clientUid: e.target.value })
                  }
                  placeholder="CHE-123.456.789"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mandateType">Mandate Type</Label>
                <Select
                  value={formData.mandateType}
                  onValueChange={(value) =>
                    setFormData({ ...formData, mandateType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_bookkeeping">
                      Full Bookkeeping
                    </SelectItem>
                    <SelectItem value="fiduciary">Fiduciary</SelectItem>
                    <SelectItem value="tax_only">Tax Only</SelectItem>
                    <SelectItem value="consulting">Consulting</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="industryCode">Industry Code</Label>
                <Input
                  id="industryCode"
                  value={formData.industryCode}
                  onChange={(e) =>
                    setFormData({ ...formData, industryCode: e.target.value })
                  }
                  placeholder="e.g., 62010"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fiscalYearEnd">Fiscal Year End</Label>
              <Select
                value={formData.fiscalYearEnd}
                onValueChange={(value) =>
                  setFormData({ ...formData, fiscalYearEnd: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12-31">December 31</SelectItem>
                  <SelectItem value="06-30">June 30</SelectItem>
                  <SelectItem value="03-31">March 31</SelectItem>
                  <SelectItem value="09-30">September 30</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between space-x-2">
              <div className="space-y-0.5">
                <Label htmlFor="vatLiable">VAT Liable</Label>
                <p className="text-sm text-muted-foreground">
                  Is this client subject to Swiss VAT?
                </p>
              </div>
              <Switch
                id="vatLiable"
                checked={formData.vatLiable}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, vatLiable: checked })
                }
              />
            </div>

            {formData.vatLiable && (
              <div className="space-y-2">
                <Label htmlFor="vatMethod">VAT Method</Label>
                <Select
                  value={formData.vatMethod}
                  onValueChange={(value) =>
                    setFormData({ ...formData, vatMethod: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="effective">Effective Method</SelectItem>
                    <SelectItem value="flat_rate">Flat Rate Method</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Client
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
